"use client";

import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BusinessContent from "./BusinessContent";
import { useInViewAnimation } from "../animation/useInViewAnimation";
import { useTranslation } from "next-i18next";
import Image from "next/image";

export default function ServiceSelection() {
  const [selected, setSelected] = useState(false);
  const containerRef = useRef(null);
  const inView = useInViewAnimation(containerRef);
  const { t } = useTranslation("service");

  return (
    <section
      id="service"
      ref={containerRef}
      className="relative w-full px-4 py-16 flex flex-col items-center font-[futura] overflow-hidden text-[#262104]"
    >
      <div className="absolute inset-0 z-5">
        <Image
          src="/img/services_bottles.png"
          alt="Services Bottles Background"
          fill
          className="object-contain opacity-100 scale-150"
          priority
        />
      </div>

      <div className="absolute inset-0 bg-reefliBeige z-0" />

      <motion.h2
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="text-xl md:text-3xl font-bold mb-10 text-center z-10"
      >
        {t("title")}
      </motion.h2>

      <motion.div
        ref={containerRef}
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="relative w-full max-w-4xl min-h-[450px] md:min-h-[600px] rounded-3xl shadow-xl flex flex-col px-4 md:px-10 py-20 md:py-10 overflow-hidden items-center z-10"
      >
        <div className="absolute inset-0 bg-white/60 backdrop-blur-xs z-10" />

        <div
          className="flex flex-1 items-center justify-center w-full h-full relative z-20"
          onMouseMove={(e) => {
            const synthetic = new MouseEvent("mousemove", e);
            document.querySelector(".blob-container")?.dispatchEvent(synthetic);
          }}
        >
          <AnimatePresence mode="wait">
            {!selected && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4 }}
                className="max-w-2xl w-full space-y-4 text-left text-sm md:text-base lg:text-lg"
              >
                <p>
                  {t("intro0")}
                  <b>
                    {" "} REEFLI
                    <span className="align-super text-[0.65em] font-sans ml-0.5">
                      ™
                    </span>
                  </b>{" "}
                  {t("intro1")}
                </p>
                <p>{t("intro2")}</p>
                <p>{t("intro3")}</p>
                <p>{t("intro4")}</p>
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 pt-1">
                  <button
                    onClick={() => setSelected(true)}
                    className="bg-gradient-to-br from-[#d7f3ff] to-[#b3e5fc] p-4 md:p-6 rounded-2xl shadow-md font-medium w-full max-w-md hover:scale-105 transition-transform text-center text-sm md:text-xl cursor-pointer"
                    dangerouslySetInnerHTML={{ __html: t("businessButton") }}
                  />
                </div>
              </motion.div>
            )}

            {selected && (
              <motion.div
                key="business"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4 }}
                className="max-w-2xl w-full space-y-4 lg:space-y-6 text-left text-sm md:text-base lg:text-lg overflow-y-auto"
              >
                <BusinessContent />
                <button
                  onClick={() => setSelected(false)}
                  className="text-sm underline hover:text-[#262104] cursor-pointer"
                >
                  {t("back")}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </section>
  );
}
